import { LABELS } from '@/i18n/labels';
import { useLangStore } from '@/stores/langStore';
import type { Conversation } from '@/types';

/**
 * Classifies a channel-type string into one of the four supported logical
 * buckets the Agent Console knows how to label and iconify. New channels
 * default to <c>web</c> so they still render.
 */
export type ChannelKind = 'email' | 'whatsapp' | 'sms' | 'web';

export function classifyChannel(channel: string | undefined | null): ChannelKind {
  const c = (channel ?? '').toLowerCase().trim();
  if (c === 'email' || c === 'mail') return 'email';
  if (c === 'whatsapp' || c === 'wa') return 'whatsapp';
  if (c === 'sms' || c === 'text' || c === 'textmessage') return 'sms';
  return 'web';
}

/**
 * Derives the header label shown at the top of a conversation row.
 * Rules (per product spec, T9.3):
 *   • Email    → subject of the email (from metadata.emailSubject)
 *   • WhatsApp → sender display name (metadata.displayName / .senderName / .fromName)
 *   • SMS      → sender display name (same sources as WhatsApp), else phone number
 *   • WebChat  → identified customer's name (W8); else "Anonymous - {first message}";
 *               else "Guest chat #NNNN" — where NNNN is the last 4 hex digits of the
 *               session id, uppercased. A raw session/customer GUID is NEVER shown.
 *
 * T9.3: the WebChat branch no longer falls back to a raw GUID (`shortSessionId`).
 * For WhatsApp/SMS the customerId fallback changed: if the customerId is a raw
 * GUID-shaped value (cust-/anon-) it is replaced by the Guest-chat label too,
 * so agents never see an opaque ID.
 */
export function getConversationLabel(conv: Conversation): string {
  const lang = useLangStore.getState().lang;
  const kind = classifyChannel(conv.channel);
  const meta = conv.metadata ?? {};

  switch (kind) {
    case 'email': {
      const subject = (meta.emailSubject || meta.subject || '').trim();
      return subject.length > 0 ? subject : LABELS.noSubject[lang];
    }
    case 'whatsapp':
    case 'sms': {
      const name = (meta.displayName || meta.senderName || meta.fromName || '').trim();
      if (name.length > 0) return name;
      // Fall back to phone number if we have one.
      const phone = (meta.phoneNumber || meta.from || '').trim();
      if (phone.length > 0) return phone;
      // Otherwise use the customerId — but never expose a raw GUID form.
      const cid = (conv.customerId ?? '').trim();
      if (!isGuidLikeId(cid)) return cid;
      return guestLabel(conv.sessionId, lang);
    }
    case 'web':
    default: {
      // W8 — identified customer: show the name captured on the session.
      const name = (conv.customerDisplayName || '').trim();
      if (name.length > 0) return name;
      // Anonymous: "Anonymous - {start of the first message}" so agents can tell
      // simultaneous anonymous sessions apart by what was actually asked.
      if (isAnonymousCustomer(conv.customerId)) {
        const firstMsg = (conv.firstMessagePreview || '').trim().replace(/\s+/g, ' ');
        if (firstMsg.length > 0) {
          const snippet = firstMsg.length > 40 ? firstMsg.substring(0, 39) + '…' : firstMsg;
          return LABELS.anonymousWithMessage[lang].replace('{msg}', snippet);
        }
        return LABELS.anonymous[lang];
      }
      // Identified-but-no-name, or any id that looks like a raw GUID → Guest label.
      // This covers the old `shortSessionId()` fallback.
      return guestLabel(conv.sessionId, lang);
    }
  }
}

/**
 * The name of the PERSON on the other end — as distinct from
 * {@link getConversationLabel}, which names the THREAD.
 *
 * The two coincide for chat and WhatsApp but diverge sharply for email, where the thread
 * label is the subject line. Handing that to the customer panel captioned a customer
 * "how do I change the language?" — so this helper never falls back to the subject, nor to
 * the first message, nor to any Guest-chat code. When we genuinely don't know who someone
 * is it returns an empty string and lets the caller say so.
 *
 * Returns only what the CONVERSATION knows; the customer panel prefers the CRM profile and
 * the customer_identities display name over this.
 */
export function getCustomerDisplayName(conv: Conversation): string {
  const explicit = (conv.customerDisplayName || '').trim();
  if (explicit.length > 0) return explicit;

  const meta = conv.metadata ?? {};
  const fromChannel = (meta.displayName || meta.senderName || meta.fromName || '').trim();
  if (fromChannel.length > 0) return fromChannel;

  // A phone number identifies a person; a subject line does not.
  if (classifyChannel(conv.channel) !== 'email') {
    const phone = (meta.phoneNumber || meta.from || '').trim();
    if (phone.length > 0) return phone;
  }

  return '';
}

/**
 * True when the customer id is an anonymous form — either the auth-time
 * "anon-{guid}" or the session-scoped memory shape "tenant:{t}:session:{s}".
 * Identified customers use the opaque "cust-{guid}" form.
 */
export function isAnonymousCustomer(customerId: string | undefined | null): boolean {
  const c = (customerId ?? '').trim();
  return c.startsWith('anon-') || (c.startsWith('tenant:') && c.includes(':session:'));
}

/**
 * True when a customerId looks like a raw GUID-shaped opaque identifier that
 * should never be shown to the agent.
 * Matches: "cust-{guid}", "anon-{guid}", and plain UUIDs (hex + hyphens).
 */
function isGuidLikeId(id: string): boolean {
  if (!id) return true;
  if (id.startsWith('cust-') || id.startsWith('anon-')) return true;
  // Plain UUID pattern e.g. "d9a35068-1ebc-40ac-ad0b-1422fe2d0080"
  return /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(id);
}

/**
 * T9.3 — "Guest chat #NNNN" (EN) / "שיחה עם אורח #NNNN" (HE) where NNNN is
 * the last 4 hex chars of the session id, uppercased. Stable per session so
 * agents can reference it ("Guest chat #A3F2 is escalating"). Never shows the
 * full raw GUID.
 */
function guestLabel(sessionId: string, lang: 'en' | 'he'): string {
  const code = deriveFriendlyCode(sessionId);
  return LABELS.guestChatLabel[lang].replace('{code}', code);
}

/**
 * Derives a short 4-char uppercase hex code from a session id that is stable
 * for the lifetime of the session (last 4 hex chars of the bare UUID).
 */
function deriveFriendlyCode(sessionId: string): string {
  const s = (sessionId ?? '').replace(/-/g, '').replace(/^(anon|cust)-/i, '');
  const hex = s.replace(/[^0-9a-fA-F]/g, '');
  if (hex.length >= 4) return hex.slice(-4).toUpperCase();
  return hex.toUpperCase().padEnd(4, '0');
}

/**
 * Produces the truncated message preview shown under the label.
 * Caps at <paramref name="maxLen"/> chars (default 80) and appends
 * <c>…</c> so long messages still give a sense of topic at a glance.
 */
export function getConversationPreview(conv: Conversation, maxLen = 80): string {
  const raw = (conv.lastMessageContent ?? '').trim();
  if (raw.length === 0) return '';
  // Collapse internal whitespace so multi-line content doesn't blow up the row.
  const flat = raw.replace(/\s+/g, ' ');
  if (flat.length <= maxLen) return flat;
  return flat.substring(0, maxLen - 1) + '…';
}
